import React from 'react';
import { NavLink, useNavigate } from 'react-router-dom';
import logo from '../../assets/logo.png';
import { LayoutDashboard, Building2, CalendarCheck, Wallet, User, LogOut, Users, QrCode, Receipt, UserCog, Star } from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';
import { useFirestoreConnection } from '../../hooks/useFirestoreConnection';
import { permissionsEffectives } from '../../utils/permissions';
import toast from 'react-hot-toast';

export default function Sidebar({ badgeCount = 0 }) {
  const { logout, userProfile } = useAuth();
  const navigate = useNavigate();
  const connected = useFirestoreConnection();
  const perms = permissionsEffectives(userProfile);
  const estGerantPrincipal = userProfile?.role !== 'sous-gerant';

  async function handleLogout() {
    await logout();
    toast.success('Déconnecté');
    navigate('/');
  }

  const navItems = [
    { to: '/gerant/dashboard',    icon: LayoutDashboard, label: 'Tableau de bord' },
    { to: '/gerant/residences',   icon: Building2,       label: 'Mes résidences' },
    perms.voirReservations && { to: '/gerant/reservations', icon: CalendarCheck, label: 'Réservations', badge: badgeCount },
    perms.voirReservations && { to: '/gerant/scanner',      icon: QrCode,        label: 'Scanner un ticket' },
    perms.ajouterLocataires && { to: '/gerant/locataires',  icon: Users,         label: 'Locataires' },
    perms.voirPaiements && { to: '/gerant/paiements',       icon: Wallet,        label: 'Paiements' },
    estGerantPrincipal && { to: '/gerant/depenses',         icon: Receipt,       label: 'Dépenses' },
    estGerantPrincipal && { to: '/gerant/avis',             icon: Star,          label: 'Avis clients' },
    estGerantPrincipal && { to: '/gerant/equipe',           icon: UserCog,       label: 'Mon équipe' },
    { to: '/gerant/profil',      icon: User,            label: 'Profil' },
  ].filter(Boolean);

  return (
    <aside style={{
      position: 'fixed',
      top: 0, left: 0, bottom: 0,
      width: 'var(--sidebar-width)',
      background: 'var(--primary)',
      display: 'flex',
      flexDirection: 'column',
      zIndex: 100,
      boxShadow: '4px 0 20px rgba(0,0,0,0.1)',
    }}>
      {/* Logo */}
      <div style={{ padding: '1.5rem 1.25rem', borderBottom: '1px solid rgba(255,255,255,0.1)' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
          <img src={logo} alt="ResiPro CI" style={{ width: 40, height: 40, borderRadius: 10, objectFit: 'contain', background: 'white', padding: 2 }} />
          <div>
            <div style={{ color: 'white', fontFamily: 'Poppins', fontWeight: 700, fontSize: '1rem' }}>ResiPro CI</div>
            <div style={{ color: 'rgba(255,255,255,0.6)', fontSize: '0.72rem' }}>Gérant</div>
          </div>
        </div>
      </div>

      {/* User info */}
      {userProfile && (
        <div style={{ padding: '1rem 1.25rem', borderBottom: '1px solid rgba(255,255,255,0.1)' }}>
          <div style={{ color: 'rgba(255,255,255,0.7)', fontSize: '0.78rem' }}>Connecté en tant que</div>
          <div style={{ color: 'white', fontWeight: 600, fontSize: '0.9rem', marginTop: 2 }}>
            {userProfile.prenom} {userProfile.nom}
          </div>
        </div>
      )}

      {/* Nav items */}
      <nav style={{ flex: 1, padding: '0.75rem', display: 'flex', flexDirection: 'column', gap: '0.25rem', overflowY: 'auto' }}>
        {navItems.map(({ to, icon: Icon, label, badge }) => (
          <NavLink key={to} to={to} style={({ isActive }) => ({
            display: 'flex', alignItems: 'center', gap: '0.75rem',
            padding: '0.7rem 0.75rem', borderRadius: 8,
            color: isActive ? 'white' : 'rgba(255,255,255,0.65)',
            background: isActive ? 'rgba(255,255,255,0.15)' : 'transparent',
            fontWeight: isActive ? 600 : 400, fontSize: '0.9rem',
            transition: 'all 0.2s', textDecoration: 'none',
          })}>
            <Icon size={18} />
            <span style={{ flex: 1 }}>{label}</span>
            {badge > 0 && (
              <span style={{
                background: '#E74C3C', color: 'white',
                fontSize: '0.68rem', fontWeight: 700,
                minWidth: 20, height: 20, borderRadius: 10,
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                padding: '0 5px', lineHeight: 1,
              }}>
                {badge > 99 ? '99+' : badge}
              </span>
            )}
          </NavLink>
        ))}
      </nav>

      {/* Connection indicator */}
      <div style={{ padding: '0.4rem 1.25rem', display: 'flex', alignItems: 'center', gap: '0.4rem' }}>
        <span style={{ width: 8, height: 8, borderRadius: '50%', background: connected ? '#2ecc71' : '#e74c3c', display: 'inline-block', flexShrink: 0 }} />
        <span style={{ fontSize: '0.7rem', color: connected ? 'rgba(255,255,255,0.5)' : '#e74c3c' }}>
          {connected ? 'Connecté' : 'Hors ligne'}
        </span>
      </div>

      {/* Signature */}
      <div style={{ padding: '0.5rem 1.25rem', textAlign: 'center' }}>
        <p style={{ fontSize: '0.65rem', color: 'rgba(255,255,255,0.3)', letterSpacing: '0.04em', lineHeight: 1.6 }}>
          <span style={{ fontWeight: 700, color: 'rgba(255,255,255,0.5)' }}>KONSTRUCT</span> by Constant<br />Powered by AI
        </p>
      </div>

      {/* Logout */}
      <div style={{ padding: '0.75rem' }}>
        <button onClick={handleLogout} style={{
          display: 'flex', alignItems: 'center', gap: '0.75rem',
          padding: '0.7rem 0.75rem', borderRadius: 8, width: '100%',
          color: 'rgba(255,255,255,0.65)', background: 'transparent',
          border: 'none', cursor: 'pointer', fontSize: '0.9rem', transition: 'all 0.2s',
        }}
          onMouseEnter={e => { e.currentTarget.style.background = 'rgba(231,76,60,0.3)'; e.currentTarget.style.color = 'white'; }}
          onMouseLeave={e => { e.currentTarget.style.background = 'transparent'; e.currentTarget.style.color = 'rgba(255,255,255,0.65)'; }}
        >
          <LogOut size={18} />
          Déconnexion
        </button>
      </div>
    </aside>
  );
}
